'use client';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Accordion, AccordionDetails, AccordionSummary, Box, Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import FaqSearchBar from './FaqSearchBar';
import { groupFaqsByCategory } from '@/containers/faq/utils/groupFaqsByCategory';
import AnimationFadeUp from '@/components/animations/AnimationFadeUp';
import type { FAQDTO } from '@/types/models';
type FaqAccordionListProps = {
  data: FAQDTO[];
};
function stripHtml(html: string) {
  return html.replace(/<[^>]*>/g, ' ');
}
function filterFaqs(faqs: FAQDTO[], search: string) {
  const query = search.trim().toLowerCase();
  if (!query) return faqs;
  return faqs.filter(faq => {
    const question = faq.question.toLowerCase();
    const answer = stripHtml(faq.answer).toLowerCase();
    return question.includes(query) || answer.includes(query);
  });
}
export default function FaqAccordionList({
  data
}: FaqAccordionListProps) {
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<number | false>(false);
  const filteredFaqs = useMemo(() => filterFaqs(data, search), [data, search]);
  const sections = useMemo(() => {
    if (search.trim()) {
      return filteredFaqs.length ? [{
        title: '',
        items: filteredFaqs
      }] : [];
    }
    return groupFaqsByCategory(data);
  }, [data, filteredFaqs, search]);
  const handleChange = (id: number) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedId(isExpanded ? id : false);
  };
  return <Stack spacing='var(--spacing_m)' data-sentry-element="Stack" data-sentry-component="FaqAccordionList" data-sentry-source-file="FaqAccordionList.tsx">
      <FaqSearchBar value={search} onChange={setSearch} resultCount={filteredFaqs.length} data-sentry-element="FaqSearchBar" data-sentry-source-file="FaqAccordionList.tsx" />

      {sections.length === 0 ? <Box sx={{
      p: 'var(--spacing_xl)',
      textAlign: 'center',
      borderRadius: 'var(--corner_radius_xl)',
      bgcolor: 'background.bg3',
      border: '1px solid',
      borderColor: 'line.primary'
    }}>
          <Typography sx={{
        typography: 'bodyLargeMedium__Wallfund',
        color: 'text.text2'
      }}>
            پرسشی با این عبارت پیدا نشد. عبارت دیگری امتحان کنید.
          </Typography>
        </Box> : <Stack spacing='var(--spacing_l)'>
          {sections.map((section, sectionIndex) => <Stack key={section.title || 'search-results'} spacing='var(--spacing_m)'>
              {section.title && <Typography component='h2' sx={{
          typography: {
            xs: 'headlineStrong__Wallfund',
            md: 'titleStrong__Wallfund'
          },
          color: 'text.text',
          textAlign: {
            xs: 'center',
            md: 'start'
          }
        }}>
                  {section.title}
                </Typography>}

              <Stack spacing='var(--spacing_s)'>
                {section.items.map((faq, index) => {
            const isExpanded = expandedId === faq.id;
            const animationDelay = Math.min((sectionIndex * 3 + index) * 0.04, 0.3);
            return <AnimationFadeUp key={faq.id} delay={animationDelay}>
                      <Accordion expanded={isExpanded} onChange={handleChange(faq.id)} disableGutters elevation={0} sx={{
                borderRadius: 'var(--corner_radius_xl) !important',
                border: '1px solid',
                borderColor: isExpanded ? 'primaryBrand.bg3' : 'line.primary',
                bgcolor: isExpanded ? 'primaryBrand.bg6' : 'background.bg',
                overflow: 'hidden',
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                '&:before': {
                  display: 'none'
                },
                '&.Mui-expanded': {
                  margin: 0
                }
              }}>
                        <AccordionSummary expandIcon={isExpanded ? <RemoveIcon sx={{
                  fontSize: 20,
                  color: 'primaryBrand.text'
                }} /> : <AddIcon sx={{
                  fontSize: 20,
                  color: 'text.text3'
                }} />} sx={{
                  px: {
                    xs: 'var(--spacing_m)',
                    md: 'var(--spacing_l)'
                  },
                  py: 'var(--spacing_xs)',
                  '& .MuiAccordionSummary-content': {
                    my: 'var(--spacing_s)'
                  }
                }}>
                          <Typography sx={{
                    typography: 'bodyLargeStrong__Wallfund',
                    color: 'text.text',
                    textAlign: 'start'
                  }}>
                            {faq.question}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{
                  px: {
                    xs: 'var(--spacing_m)',
                    md: 'var(--spacing_l)'
                  },
                  pt: 0,
                  pb: 'var(--spacing_m)'
                }}>
                          <Typography component='div' sx={{
                    typography: 'bodyRegular__Wallfund',
                    color: 'text.text2',
                    lineHeight: 1.9,
                    textAlign: 'justify',
                    '& a': {
                      color: 'primaryBrand.text',
                      fontWeight: 600,
                      textDecoration: 'none',
                      '&:hover': {
                        textDecoration: 'underline'
                      }
                    }
                  }} dangerouslySetInnerHTML={{
                    __html: faq.answer
                  }} />
                        </AccordionDetails>
                      </Accordion>
                    </AnimationFadeUp>;
          })}
              </Stack>
            </Stack>)}
        </Stack>}
    </Stack>;
}